import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Section, Mono, Glass, useAudience, Swap } from "./NetworkAtoms";

const BEATS = [
  {
    k: "Case submitted",
    intro: "Your client's case goes in — by you in two minutes, or by them through your own link with their documents attached.",
    lender: "A vetted introducer submits a structured case: amount, term, sector, purpose, borrowing already in place, documents, consent.",
    detail: ["Reference minted", "Consent timestamped", "Documents attached"],
    metric: ["Structured fields", "27"],
  },
  {
    k: "Fit-scored",
    intro: "Before anyone sees it, we score the case against every lender's live criteria — band, sector, term, geography, appetite.",
    lender: "We score it against your criteria before it reaches you, so nothing lands on your desk that you'd never fund.",
    detail: ["Amount band", "Sector appetite", "Term + geography"],
    metric: ["Top match", "92"],
  },
  {
    k: "Routed to the panel",
    intro: "Auto-routing places it with the desks most likely to say yes. You can see exactly who has it and when they got it.",
    lender: "It arrives on your desk with the reasoning visible — and only to the desks that fit, so you're not racing eight others.",
    detail: ["Rule matched", "Lenders notified", "Push + email + SMS"],
    metric: ["Desks selected", "3"],
  },
  {
    k: "Lender reviews",
    intro: "Status moves in front of you. If a desk goes quiet, we chase them — automatically, every two days, up to three times.",
    lender: "Work it in the portal or your CRM. One control moves it from review to offer, and the introducer is told the moment you do.",
    detail: ["In review", "Chased automatically", "Message the desk"],
    metric: ["Chase cadence", "48h"],
  },
  {
    k: "Offer made",
    intro: "Terms come back and land in your portal and inbox at the same moment. You talk to your client; we keep the record.",
    lender: "Log the offer once. The introducer, the audit trail and the commission calculation all update from that single action.",
    detail: ["Offer logged", "Introducer notified", "Audit entry written"],
    metric: ["Stage", "Offer"],
  },
  {
    k: "Completion",
    intro: "The deal completes and your commission is calculated from your live terms — no invoice to raise, no maths to do.",
    lender: "Mark it funded and everything downstream happens on its own: statements, notifications, reconciliation.",
    detail: ["Funded value recorded", "Commission calculated", "CRM synced"],
    metric: ["Funded", "£1.85m"],
  },
  {
    k: "Statement and payment",
    intro: "A branded PDF statement is generated and emailed on your payout schedule, then paid to the bank details you hold in the portal.",
    lender: "Your fee position is stated the same way — one document per period, generated from the same numbers we both saw.",
    detail: ["PDF statement issued", "Payout scheduled", "Every version filed"],
    metric: ["Statement", "Auto"],
  },
];

/** Below lg the sticky visual is gone, so each beat carries its own card — and must stay legible. */
const useStacked = () => {
  const [stacked, setStacked] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const onChange = (e) => setStacked(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return stacked;
};

export default function JourneyScroll() {
  const { audience } = useAudience();
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();
  const stacked = useStacked();
  // Spotlighting one beat works beside the sticky card; on a phone it just makes six of the seven
  // steps look broken, so everything stays readable and the blue number marks where you are.
  const resting = stacked ? 0.72 : 0.25;

  return (
    <Section eyebrow="The full journey" testId="network-journey"
             title={<Swap k={`jr-${audience}`}>
               {audience === "lender" ? "From their introduction to your funded deal" : "From your introduction to your payment"}
             </Swap>}
             lede="Seven moves. Every one of them already built, already automated, already logged.">
      <div className="mt-14 grid lg:grid-cols-12 gap-10">
        {/* Scrolling narrative */}
        <div className="lg:col-span-6 space-y-14 sm:space-y-20">
          {BEATS.map((b, i) => (
            <motion.div
              key={b.k}
              data-testid={`journey-beat-${i}`}
              initial={reduce ? false : { opacity: resting, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ margin: "-45% 0px -45% 0px" }}
              onViewportEnter={() => setActive(i)}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* On a phone the number sits above the title, so the whole beat shares one left
                  edge and the card is centred in the band instead of shunted right. */}
              <div className={`lg:hidden font-mono text-[11px] tabular-nums ${active === i ? "text-[#0055FF]" : "text-slate-500"}`}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="flex items-baseline gap-4">
                <span className={`hidden lg:inline font-mono text-[11px] tabular-nums ${active === i ? "text-[#0055FF]" : "text-slate-600"}`}>
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="mt-1 lg:mt-0 font-cabinet text-[26px] sm:text-[32px] leading-[1.08] tracking-[-0.025em] font-semibold text-white">
                  {b.k}
                </h3>
              </div>
              <p className="mt-3 lg:ml-[2.1rem] text-[14.5px] leading-relaxed text-slate-400 max-w-xl">
                {audience === "lender" ? b.lender : b.intro}
              </p>
              {/* mobile inline visual */}
              <div className="lg:hidden mt-5">
                <BeatCard beat={b} index={i} live={active === i} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Sticky visual */}
        <div className="hidden lg:block lg:col-span-6">
          <div className="sticky top-28">
            <div className="flex gap-6">
              {/* rail */}
              <div className="relative w-6 shrink-0 pt-2">
                <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2 bg-white/[0.09]" />
                <motion.div
                  className="absolute left-1/2 top-0 w-px -translate-x-1/2 bg-gradient-to-b from-[#0055FF] to-[#7da9ff]"
                  animate={{ height: `${((active + 1) / BEATS.length) * 100}%` }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                />
                <div className="relative flex flex-col justify-between h-[300px]">
                  {BEATS.map((_, i) => (
                    <span key={i}
                          className={`size-2 rounded-full mx-auto transition-all duration-500 ${
                            i <= active ? "bg-[#0055FF] scale-100 shadow-[0_0_12px_rgba(0,85,255,0.9)]" : "bg-white/15 scale-90"}`} />
                  ))}
                </div>
              </div>
              <div className="flex-1">
                <BeatCard beat={BEATS[active]} index={active} big />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function BeatCard({ beat, index, big = false, live = false }) {
  const [label, value] = beat.metric;
  return (
    <Glass hover={false} testId={`journey-card-${index}`}
           className={`overflow-hidden transition-shadow duration-500 ${big ? "p-7" : "p-5"} ${
             live ? "ring-1 ring-[#0055FF]/35 shadow-[0_0_26px_-10px_rgba(0,85,255,0.55)]" : ""}`}>
      <div className="flex items-center justify-between">
        <Mono>{`Step 0${index + 1}`}</Mono>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600">Connexis engine</span>
      </div>

      <motion.div key={beat.k} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}>
        <div className={`mt-5 font-cabinet ${big ? "text-[22px]" : "text-[18px]"} font-semibold text-white tracking-[-0.02em]`}>
          {beat.k}
        </div>

        <div className="mt-4 space-y-2">
          {beat.detail.map((d, i) => (
            <motion.div key={d}
                        initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: 0.08 * i }}
                        className="flex items-center gap-2.5 rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-2">
              <span className="size-1.5 rounded-full bg-[#0055FF]" />
              <span className="text-[12.5px] text-slate-300">{d}</span>
            </motion.div>
          ))}
        </div>

        <div className="mt-5 flex items-end justify-between border-t border-white/[0.07] pt-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">{label}</span>
          <span className={`font-mono tabular-nums font-semibold text-white ${big ? "text-[24px]" : "text-[18px]"}`}>{value}</span>
        </div>
      </motion.div>
    </Glass>
  );
}
